import React, {useState} from 'react';
import {FormattedMessage, defineMessages} from 'react-intl';
import {
  css as cssDeprecated,
  Banner,
  Button,
  ButtonGroup,
  FormFieldGroup,
  CountryCode,
  Link,
  Spinner,
} from '@sail/ui/deprecated';
import {css, view, TextField, TextArea, PhoneNumberField} from '@sail/ui';
import {isValidPhoneNumber} from 'libphonenumber-js';
import {useTheme} from '../ThemeProvider';
import {CloseButton} from '../Widget/CloseButton';

const messages = defineMessages({
  title: {
    id: 'conversations_ui.rac.title',
    description: 'Title for the request-a-call form',
    defaultMessage: 'Have us call you',
  },
  nameLabel: {
    id: 'conversations_ui.rac.nameLabel',
    description: "Label for the request-a-call form's name input",
    defaultMessage: 'Name',
  },
  numberLabel: {
    id: 'conversations_ui.rac.numberLabel',
    description: "Label for the request-a-call form's number input",
    defaultMessage: 'Phone number',
  },
  descriptionLabel: {
    id: 'conversations_ui.rac.descriptionLabel',
    description: "Label for the request-a-call form's description input",
    defaultMessage: 'How can we help?',
  },
  cancelLabel: {
    id: 'conversations_ui.rac.cancelLabel',
    description: "Label for the request-a-call form's cancel button",
    defaultMessage: 'Cancel',
  },
  submitLabel: {
    id: 'conversations_ui.rac.submitLabel',
    description: "Label for the request-a-call form's submit button",
    defaultMessage: 'Request a call',
  },
  updatedLegalCopy: {
    id: 'conversations_ui.rac.recordingDisclaimer',
    description: 'Legal copy that will appear under the "Call me" button',
    defaultMessage:
      'This call may be recorded and monitored for quality and training purposes. The recording will be processed in accordance with our <Link>Privacy Policy</Link>. <Link2>Read more</Link2>.',
  },
});

const SubmitButton = ({
  onSubmit,
  disabled = false,
  loading = false,
}: {
  onSubmit: () => void;
  disabled: boolean;
  loading: boolean;
}) => {
  const theme = useTheme();
  return (
    <Button
      type="primary"
      size="medium"
      css={{
        width: cssDeprecated.value('100%'),
        boxSizing: cssDeprecated.value('border-box'),
        color: cssDeprecated.value(theme.onCtaTextColor),
        backgroundColor: cssDeprecated.value(theme.ctaColor),
        borderColor: cssDeprecated.value(theme.ctaColor),
        outlineColor: cssDeprecated.value(theme.ctaColor),
        '--button-background-color': cssDeprecated.value(theme.ctaColor),
        '--keyline': cssDeprecated.value(theme.ctaColor),
        keyline: cssDeprecated.value(theme.ctaColor),
        '&:hover': {
          backgroundColor: cssDeprecated.value(theme.ctaHoverColor),
        },
        '&:focus': {
          outline: theme.focusVisibleOutline,
          focusRing: theme.focusVisibleOutline
            ? cssDeprecated.value('none')
            : undefined,
        },
      }}
      onPress={onSubmit}
      disabled={loading || disabled}
      data-testid="request-a-call-submit-button"
    >
      {loading ? (
        <Spinner css={{iconColor: 'white'}} />
      ) : (
        <FormattedMessage {...messages.submitLabel} />
      )}
    </Button>
  );
};

const LegalCopy = () => {
  const theme = useTheme();
  return (
    <view.span css={{font: 'label.small', color: 'subdued'}}>
      <FormattedMessage
        {...messages.updatedLegalCopy}
        values={{
          Link: (label: React.ReactNode) => (
            <Link
              href="//stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              type="secondary"
              style={{
                color: theme.linkColor,
              }}
            >
              {label}
            </Link>
          ),
          Link2: (label: React.ReactNode) => (
            <Link
              href="https://stripe.com/legal/privacy-center#does-stripe-record-calls"
              target="_blank"
              rel="noopener noreferrer"
              type="secondary"
              style={{
                color: theme.linkColor,
              }}
            >
              {label}
            </Link>
          ),
        }}
      />
    </view.span>
  );
};

const ErrorBanner = ({errorMessage}: {errorMessage: React.ReactNode}) => {
  return (
    <view.div>
      <Banner type="critical" description={errorMessage} />
    </view.div>
  );
};
interface Props {
  initialName?: string;
  initialPhoneNumber?: string;
  initialMessage?: string;
  defaultCountry?: CountryCode;
  handleSubmit: (
    name: string,
    phoneNumber: string,
    message: string,
  ) => Promise<void>;
  handleCancel?: () => void;
  hideCancelButton?: boolean;
  clientError?: React.ReactNode;
  serverError?: React.ReactNode;
  loading?: boolean;
}

export function Form({
  initialName,
  initialPhoneNumber,
  initialMessage,
  defaultCountry,
  handleSubmit,
  handleCancel,
  clientError,
  serverError,
  loading,
  hideCancelButton = false,
}: Props) {
  const theme = useTheme();
  const [name, setName] = useState<string>(initialName || '');
  const [phoneNumber, setPhoneNumber] = useState<string>(
    initialPhoneNumber || '',
  );
  const [message, setMessage] = useState<string>(initialMessage || '');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const onSubmit = async () => {
    setSubmitting(true);
    await handleSubmit(name, phoneNumber, message);
    setSubmitting(false);
  };

  const isValidInput = name && message && isValidPhoneNumber(phoneNumber);

  return (
    <view.form
      css={{
        stack: 'y',
        gap: 'medium',
        position: 'relative',
      }}
    >
      <view.div
        css={{
          stack: 'y',
          alignX: 'stretch',
          gap: 'small',
        }}
      >
        <view.span css={{font: 'label.large.emphasized'}}>
          <FormattedMessage {...messages.title} />
        </view.span>

        <LegalCopy />
      </view.div>
      {serverError ? <ErrorBanner errorMessage={serverError} /> : null}
      <FormFieldGroup layout="column" error={clientError}>
        <view.div
          data-testid="clintonm-sample"
          css={{
            stack: 'y',
            alignX: 'stretch',
            gap: 'medium',
          }}
        >
          <TextField
            data-testid="request-a-call-name-input"
            label={<FormattedMessage {...messages.nameLabel} />}
            defaultValue={initialName}
            onChange={(e) => setName(e.target.value)}
          />
          <PhoneNumberField
            data-testid="request-a-call-phone-input"
            label={<FormattedMessage {...messages.numberLabel} />}
            defaultCountry={defaultCountry || 'US'}
            defaultValue={initialPhoneNumber}
            onChange={setPhoneNumber}
            // The PhoneNumberField is made up of multiple fields, so we need to do some
            // special styling to get everything aligned to the left properly while still
            // stretching to fill space
            uses={[
              css.global({
                '[data-testid="request-a-call-phone-input"] > div:first-of-type':
                  {
                    justifyContent: 'flex-start !important',
                  },
              }),
            ]}
          />
          <TextArea
            data-testid="request-a-call-text-area"
            label={<FormattedMessage {...messages.descriptionLabel} />}
            defaultValue={initialMessage}
            resizeable
            onChange={(e) => setMessage(e.target.value)}
          />
        </view.div>
      </FormFieldGroup>
      <view.div
        css={{
          stack: 'y',
          alignX: 'stretch',
          gap: 'small',
        }}
      >
        <ButtonGroup
          direction="column"
          css={{width: cssDeprecated.value('100%')}}
        >
          <SubmitButton
            onSubmit={onSubmit}
            loading={submitting || !!loading}
            disabled={!isValidInput}
          />
          {handleCancel && !hideCancelButton ? (
            <Button
              type="secondary"
              size="medium"
              css={{
                width: cssDeprecated.value('100%'),
                boxSizing: cssDeprecated.value('border-box'),
                '&:focus': {
                  outline: theme.focusVisibleOutline,
                  focusRing: theme.focusVisibleOutline
                    ? cssDeprecated.value('none')
                    : undefined,
                },
              }}
              onPress={handleCancel}
              data-testid="request-a-call-cancel-button"
            >
              <FormattedMessage {...messages.cancelLabel} />
            </Button>
          ) : null}
        </ButtonGroup>
      </view.div>
      {handleCancel ? (
        <CloseButton
          onClick={handleCancel}
          dataTestId="request-a-call-x-cancel-button"
        />
      ) : null}
    </view.form>
  );
}
